import {
  SupplierPartAvailabilityResponseDto,
  OfflineSupplierEntry,
} from "../types";

export const buildOfflineSupplierMap = (
  response: SupplierPartAvailabilityResponseDto | undefined | null,
  allSuppliers: any[],
  _remainingParts?: Array<{ rootPartNumber: string }>,
  excludeSupplierIds?: string[],
): Map<string, OfflineSupplierEntry> => {
  const map = new Map<string, OfflineSupplierEntry>();

  if (
    !response ||
    !Array.isArray(response.results) ||
    response.results.length === 0
  ) {
    return map;
  }

  const excludeSet =
    excludeSupplierIds && excludeSupplierIds.length > 0
      ? new Set(excludeSupplierIds)
      : null;

  const supplierLookup = new Map<
    string,
    { businessName: string; lat?: number; lng?: number }
  >();
  if (Array.isArray(allSuppliers)) {
    allSuppliers.forEach((supplier: any) => {
      if (!supplier._id) return;
      const name = supplier.businessName || supplier.name || "Unknown Supplier";
      const loc = supplier.address?.location;
      const lat = loc?.latitude;
      const lng = loc?.longitude;
      supplierLookup.set(supplier._id, {
        businessName: name,
        lat: typeof lat === "number" ? lat : undefined,
        lng: typeof lng === "number" ? lng : undefined,
      });
    });
  }

  for (const result of response.results) {
    if (!result.supplierId || result.rootPartNumber == null) continue;
    if (excludeSet?.has(result.supplierId)) continue;

    const supplierInfo = supplierLookup.get(result.supplierId);
    const supplierName = supplierInfo?.businessName || "Unknown Supplier";
    const coords =
      supplierInfo?.lat != null && supplierInfo?.lng != null
        ? `${supplierInfo.lat},${supplierInfo.lng}`
        : "";
    const groupKey = `${supplierName}::${result.supplierId}::${coords}`;

    const updatedAtStr = (() => {
      if (typeof result.updatedAt === "string") return result.updatedAt;
      if (result.updatedAt == null) return undefined;
      const date = new Date(result.updatedAt);
      return isNaN(date.getTime()) ? undefined : date.toISOString();
    })();

    const existing = map.get(groupKey);
    const familyQuantity = Number(result.familyQuantity) || 0;

    if (existing) {
      const idx = existing.parts.findIndex(
        (p) => p.rootPartNumber === result.rootPartNumber,
      );
      if (idx >= 0) {
        existing.parts[idx].familyQuantity += familyQuantity;
      } else {
        existing.parts.push({
          rootPartNumber: result.rootPartNumber,
          familyQuantity,
        });
      }
      if (
        updatedAtStr &&
        (!existing.updatedAt || updatedAtStr > existing.updatedAt)
      ) {
        existing.updatedAt = updatedAtStr;
      }
    } else {
      map.set(groupKey, {
        supplierId: result.supplierId,
        supplierName,
        coords,
        groupKey,
        parts: [{ rootPartNumber: result.rootPartNumber, familyQuantity }],
        updatedAt: updatedAtStr,
      });
    }
  }

  return map;
};

export const isTopQualifiedFromParts = (
  parts: Array<{ rootPartNumber: string; familyQuantity?: number }>,
  preFacetedPartNumbersMap: Map<string, number>,
): boolean => {
  if (preFacetedPartNumbersMap.size === 0) return false;

  for (const [rootPartNumber, orderedQty] of Array.from(
    preFacetedPartNumbersMap.entries(),
  )) {
    const part = parts.find((p) => p.rootPartNumber === rootPartNumber);
    const available = part?.familyQuantity ?? 0;
    if (available < orderedQty) return false;
  }
  return true;
};

const toUpdatedAtTimestamp = (updatedAt?: string): number | undefined => {
  if (!updatedAt) return undefined;
  const ms = new Date(updatedAt).getTime();
  return isNaN(ms) ? undefined : Math.floor(ms);
};

export const createSyntheticTopHit = (entry: OfflineSupplierEntry) => {
  const updatedAt = toUpdatedAtTimestamp(entry.updatedAt);
  return {
    group_key: [entry.groupKey],
    hits: entry.parts.map((p) => ({
      rootPartNumber: p.rootPartNumber,
      familyQuantity: p.familyQuantity,
      supplierInfo: entry.groupKey,
      ...(updatedAt != null && { updatedAt }),
    })),
    found: entry.parts.length,
  };
};

export const createSyntheticInstantSearchHit = (
  entry: OfflineSupplierEntry,
) => {
  const updatedAt = toUpdatedAtTimestamp(entry.updatedAt);
  return {
    _group_key: [entry.groupKey],
    _grouped_hits: entry.parts.map((p) => ({
      rootPartNumber: p.rootPartNumber,
      familyQuantity: p.familyQuantity,
      supplierInfo: entry.groupKey,
      ...(updatedAt != null && { updatedAt }),
    })),
    _group_found: entry.parts.length,
  };
};

export const mergeOfflinePartsIntoHits = (
  existingParts: any[],
  offlineParts: Array<{ rootPartNumber: string; familyQuantity: number }>,
  updatedAt?: string,
): any[] => {
  const merged = [...existingParts];
  const updatedAtTimestamp = toUpdatedAtTimestamp(updatedAt);

  for (const offlinePart of offlineParts) {
    const idx = merged.findIndex(
      (p: any) => p.rootPartNumber === offlinePart.rootPartNumber,
    );
    if (idx >= 0) {
      merged[idx] = {
        ...merged[idx],
        familyQuantity: offlinePart.familyQuantity,
      };
    } else {
      merged.push({
        rootPartNumber: offlinePart.rootPartNumber,
        familyQuantity: offlinePart.familyQuantity,
        ...(updatedAtTimestamp != null && { updatedAt: updatedAtTimestamp }),
      });
    }
  }

  return merged;
};

export const computeOfflineFulfilledCount = (
  parts: Array<{ rootPartNumber: string; familyQuantity?: number }>,
  preFacetedPartNumbersMap: Map<string, number>,
): number => {
  let count = 0;
  for (const part of parts) {
    const orderedQty = preFacetedPartNumbersMap.get(part.rootPartNumber);
    const availableQty = part.familyQuantity ?? 0;
    if (orderedQty != null && availableQty >= orderedQty) {
      count++;
    }
  }
  return count;
};
